import React, { useEffect, useState, useCallback } from 'react';
import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import "react-data-table-component-extensions/dist/index.css";
import '../../modalright.css';
import '../../popup.css';
import '../../pricing-strategies.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function PriceRangesList() {
    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    
    // State variables
    const [priceRanges, setPriceRanges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [pricingModelFilter, setPricingModelFilter] = useState('all');
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [rangeToDelete, setRangeToDelete] = useState(null);

    // Fetch all price ranges on component mount
    useEffect(() => {
        fetchPriceRanges();
    }, [statusFilter, pricingModelFilter]);

    // Fetch price ranges from API
    const fetchPriceRanges = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}api/pricing/ranges`);
            
            if (response.data.success) {
                let rangesData = response.data.data || [];
                
                if (statusFilter === 'active') {
                    rangesData = rangesData.filter(range => range.isActive);
                } else if (statusFilter === 'inactive') {
                    rangesData = rangesData.filter(range => !range.isActive);
                }
                
                if (pricingModelFilter !== 'all') {
                    rangesData = rangesData.filter(range => range.pricingModel === pricingModelFilter);
                }
                
                setPriceRanges(rangesData);
            } else {
                setError('Failed to fetch price ranges');
            }
        } catch (err) {
            console.error('Error fetching price ranges:', err);
            setError('Error fetching price ranges. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Delete single price range
    const handleDeleteRange = async () => {
        if (!rangeToDelete) return;
        
        try {
            const response = await axios.delete(`${API_URL}api/pricing/ranges/${rangeToDelete.id}`);
            
            if (response.data.success) {
                setPriceRanges(priceRanges.filter(r => r.id !== rangeToDelete.id));
                alert('Price range deleted successfully');
            } else {
                alert('Failed to delete price range');
            }
        } catch (err) {
            console.error('Error deleting price range:', err);
            alert('Error deleting price range. Please try again.');
        } finally {
            setDeleteModalOpen(false);
            setRangeToDelete(null);
        }
    };

    // Handle row selection
    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    // Navigate to create page
    const handleAddRange = () => {
        navigate('/orders/price-ranges/create');
    };

    // Navigate to edit page
    const handleEditRange = (range) => {
        navigate(`/orders/price-ranges/edit/${range.id}`);
    };

    // Open delete confirmation modal
    const openDeleteModal = (range) => {
        setRangeToDelete(range);
        setDeleteModalOpen(true);
    };

    // Format currency
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-AE', {
            style: 'currency',
            currency: 'AED',
        }).format(value || 0);
    };

    // DataTable columns
    const columns = [
        {
            name: 'Range Name',
            selector: row => row.rangeName,
            sortable: true,
            cell: row => (
                <div style={{ fontWeight: '500' }}>{row.rangeName}</div>
            ),
        },
        {
            name: 'Zone',
            selector: row => row.zoneName || row.zoneCode || '-',
            sortable: true,
        },
        {
            name: 'Service',
            selector: row => row.serviceName || row.serviceCode || '-',
            sortable: true,
        },
        {
            name: 'Pricing Model',
            selector: row => row.pricingModel,
            sortable: true,
            cell: row => (
                <span className={`pricing-model-badge ${row.pricingModel?.toLowerCase()}`}>
                    {row.pricingModel || '-'}
                </span>
            ),
        },
        {
            name: 'Min Value',
            selector: row => row.minValue,
            sortable: true,
            cell: row => row.minValue || '0',
        },
        {
            name: 'Max Value',
            selector: row => row.maxValue,
            sortable: true,
            cell: row => row.maxValue || '∞',
        },
        {
            name: 'Base Price',
            selector: row => row.basePrice,
            sortable: true,
            cell: row => formatCurrency(row.basePrice),
        },
        {
            name: 'Per Unit Price',
            selector: row => row.perUnitPrice,
            sortable: true,
            cell: row => formatCurrency(row.perUnitPrice),
        },
        {
            name: 'Status',
            selector: row => row.isActive,
            sortable: true,
            cell: row => (
                <span className={`status-badge ${row.isActive ? 'status-active' : 'status-inactive'}`}>
                    {row.isActive ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="action-buttons">
                    <button 
                        className="btn-action btn-edit"
                        onClick={() => handleEditRange(row)}
                        title="Edit Price Range"
                    >
                        <i className="fa fa-pencil" aria-hidden="true"></i>
                    </button>
                    <button 
                        className="btn-action btn-delete"
                        onClick={() => openDeleteModal(row)}
                        title="Delete Price Range"
                    >
                        <i className="fa fa-trash" aria-hidden="true"></i>
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    // Custom styles for DataTable
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
            },
        },
        headCells: {
            style: {
                fontWeight: '600',
                fontSize: '14px',
                color: '#495057',
            },
        },
        rows: {
            style: {
                minHeight: '52px',
                '&:hover': {
                    backgroundColor: '#f1f3f4',
                },
            },
        },
    };

    const tableData = {
        columns,
        data: priceRanges,
    };

    return (
        <div className="zones-list-container">
            {/* Page Header */}
            <div className="page-header">
                <div className="header-left">
                    <h2>
                        <i className="fa fa-tags" aria-hidden="true"></i>
                        Price Ranges
                    </h2>
                    <p className="subtitle">Configure pricing by weight, piece count, or dimensions</p>
                </div>
                <div className="header-right">
                    <button className="btn btn-primary btn-add-zone" onClick={handleAddRange}>
                        <i className="fa fa-plus" aria-hidden="true"></i>
                        Add Price Range
                    </button>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="filters-section">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search price ranges..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button className="btn-search">
                        <i className="fa fa-search" aria-hidden="true"></i>
                    </button>
                </div>
                
                <div className="filter-dropdown">
                    <label>Pricing Model:</label>
                    <select 
                        value={pricingModelFilter} 
                        onChange={(e) => setPricingModelFilter(e.target.value)}
                    >
                        <option value="all">All Models</option>
                        <option value="WEIGHT">Weight Based</option>
                        <option value="PIECE">Piece Count</option>
                        <option value="DIMENSION">Dimension Based</option>
                        <option value="FLAT">Flat Rate</option>
                    </select>
                </div>
                
                <div className="filter-dropdown">
                    <label>Status:</label>
                    <select 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="alert alert-danger">
                    <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                    {error}
                </div>
            )}

            {/* DataTable */}
            <div className="table-container">
                <DataTableExtensions {...tableData} print={false} export={true} filterHidden={true}>
                    <DataTable
                        columns={columns}
                        data={priceRanges}
                        pagination
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        selectableRows
                        selectableRowsHighlight
                        onSelectedRowsChange={handleRowSelected}
                        clearSelectedRows={toggleCleared}
                        progressPending={loading}
                        progressComponent={
                            <div className="loading-spinner">
                                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                                Loading price ranges...
                            </div>
                        }
                        noDataComponent={
                            <div className="no-data">
                                <i className="fa fa-tags" aria-hidden="true"></i>
                                <p>No price ranges found</p>
                                <button className="btn btn-primary" onClick={handleAddRange}>
                                    Create First Price Range
                                </button>
                            </div>
                        }
                        customStyles={customStyles}
                        highlightOnHover
                        pointerOnHover
                    />
                </DataTableExtensions>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content delete-modal">
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                            <button className="close-btn" onClick={() => setDeleteModalOpen(false)}>
                                <i className="fa fa-times" aria-hidden="true"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete price range <strong>"{rangeToDelete?.rangeName}"</strong>?</p>
                            <p className="warning-text">
                                <i className="fa fa-exclamation-triangle" aria-hidden="true"></i>
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setDeleteModalOpen(false)}>
                                Cancel
                            </button>
                            <button className="btn btn-danger" onClick={handleDeleteRange}>
                                Delete Price Range
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
